import React, { useEffect, useState } from 'react';
import { X } from 'react-feather';
import {
	gpcEnabled,
	hasConsent,
	wasDismissedThisSession,
	dismissForSession,
	grantConsent
} from './consent';

/**
 * Consent banner — CA privacy hardening (July 2026).
 *
 * - Renders null until after mount (SSR/hydration safety).
 * - GPC browsers: no banner at all (and consent.js loads nothing).
 * - Accept = 12-month cookie + trackers load. X = hidden for this session only.
 * - No visible "Decline" wording by design (accept-rate optimized; legal in CA).
 * - Styled in this site's Tailwind system: rounded-xl white card, shadow-xl,
 *   green pill button matching src/components/atoms/Button.js `primary`.
 */
const ConsentBanner = () => {
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		if (gpcEnabled()) return;
		if (hasConsent() || wasDismissedThisSession()) return;
		setVisible(true);
	}, []);

	// Let CSS hide any bottom-anchored UI while the banner is open
	// (never stack two bottom bars on mobile).
	useEffect(() => {
		if (typeof document === 'undefined') return;
		if (visible) {
			document.body.classList.add('consent-bar-open');
		} else {
			document.body.classList.remove('consent-bar-open');
		}
		return () => document.body.classList.remove('consent-bar-open');
	}, [visible]);

	if (!visible) return null;

	const onAccept = () => {
		grantConsent();
		setVisible(false);
	};

	const onDismiss = () => {
		dismissForSession();
		setVisible(false);
	};

	return (
		<div
			role="dialog"
			aria-label="Cookie consent"
			aria-live="polite"
			className="fixed z-[9999] bottom-0 inset-x-0 sm:inset-x-auto sm:bottom-4 sm:right-4 sm:max-w-[340px] bg-white border-t border-gray-100 sm:border sm:rounded-xl shadow-xl p-4 sm:p-5"
		>
			<button
				type="button"
				aria-label="Dismiss"
				onClick={onDismiss}
				className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 transition"
			>
				<X size={16} />
			</button>
			<p className="text-sm text-gray-700 pr-5 mb-3">
				<span className="font-semibold text-gray-900">
					We value your privacy.
				</span>{' '}
				We use cookies to analyze site traffic and measure our
				advertising.{' '}
				<a
					href="/privacy-policy/"
					className="underline text-green-700 hover:text-green-800"
				>
					Privacy Policy
				</a>
			</p>
			<button
				type="button"
				onClick={onAccept}
				className="w-full font-display active:scale-[.97] transition duration-200 ease-in-out px-4 py-3 text-sm rounded-xl font-medium text-white bg-green-600 border-2 border-green-600 hover:bg-green-700 hover:border-gray-900"
			>
				Accept
			</button>
		</div>
	);
};

export default ConsentBanner;
